import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";

import { AuthProvider } from "./context/AuthContext";
import { SiteProvider, PageTracker, useSite } from "./context/SiteContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { CookieConsent } from "./components/CookieConsent";
import { Toaster } from "./components/ui/sonner";

import HomePage from "./pages/HomePage";
import FonctionnementPage from "./pages/FonctionnementPage";
import FonctionnalitesPage from "./pages/FonctionnalitesPage";
import BoutiquePage from "./pages/BoutiquePage";
import ProductPage from "./pages/ProductPage";
import AvisPage from "./pages/AvisPage";
import ReviewReturnPage from "./pages/ReviewReturnPage";
import LegalPage from "./pages/LegalPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MyAccountPage from "./pages/MyAccountPage";

import AdminLayout from "./admin/AdminLayout";
import Dashboard from "./admin/Dashboard";
import Users from "./admin/Users";
import Media from "./admin/Media";
import Content from "./admin/Content";
import Faq from "./admin/Faq";
import Offers from "./admin/Offers";
import StoreLinks from "./admin/StoreLinks";
import Testimonials from "./admin/Testimonials";
import Messages from "./admin/Messages";
import Audit from "./admin/Audit";
import Settings from "./admin/Settings";
import Profile from "./admin/Profile";
import Boutique from "./admin/Boutique";
import Audience from "./admin/Audience";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        if (!window.location.hash) window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function PublicLayout() {
    return (
        <>
            <Navbar />
            <Outlet />
            <Footer />
            <CookieConsent />
        </>
    );
}

/** Prévisualisation des brouillons CMS : URL à jeton, bannière explicite, noindex.
 * La bannière ne s'affiche que si le serveur a validé le jeton (bootstrap.preview). */
function PreviewLayout() {
    const { data } = useSite();
    useEffect(() => {
        const m = document.createElement("meta");
        m.name = "robots";
        m.content = "noindex, nofollow";
        document.head.appendChild(m);
        return () => m.remove();
    }, []);
    return (
        <>
            <Navbar />
            <Outlet />
            <Footer />
            {data && data.preview === true && (
                <div className="sticky bottom-0 z-[60] bg-forest text-ivory text-xs sm:text-sm py-2.5 px-4 flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-testid="preview-banner">
                    <span className="font-semibold">Mode prévisualisation</span>
                    <span className="text-ivory/80">les brouillons non publiés sont visibles — page non indexée et inaccessible au public.</span>
                    <a href="/" className="underline text-gold-light" data-testid="preview-exit">Quitter</a>
                </div>
            )}
            {data && data.preview !== true && (
                <div className="sticky bottom-0 z-[60] bg-red-900 text-ivory text-xs sm:text-sm py-2.5 px-4 flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-testid="preview-invalid-banner">
                    <span className="font-semibold">Jeton de prévisualisation invalide</span>
                    <span className="text-ivory/80">le contenu publié est affiché. Générez un nouveau lien depuis l'administration.</span>
                    <a href="/" className="underline text-gold-light">Quitter</a>
                </div>
            )}
        </>
    );
}

function NotFound() {
    return (
        <main className="min-h-screen bg-ivory text-ink flex flex-col items-center justify-center gap-4" data-testid="not-found">
            <h1 className="font-heading font-extrabold text-5xl">404</h1>
            <p className="text-ink-soft">Cette page n'existe pas.</p>
            <a href="/" className="text-forest underline" data-testid="notfound-home-link">Retour à l'accueil</a>
        </main>
    );
}

function AppRouter() {
    const location = useLocation();
    // OAuth callback: detect session_id synchronously during render to avoid race conditions
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/fonctionnement" element={<FonctionnementPage />} />
                <Route path="/fonctionnalites" element={<FonctionnalitesPage />} />
                <Route path="/boutique" element={<BoutiquePage />} />
                <Route path="/boutique/:slug" element={<ProductPage />} />
                <Route path="/avis" element={<AvisPage />} />
                <Route path="/confidentialite" element={<LegalPage slug="confidentialite" />} />
                <Route path="/mentions-legales" element={<LegalPage slug="mentions-legales" />} />
                <Route path="/cookies" element={<LegalPage slug="cookies" />} />
            </Route>
            <Route element={<PreviewLayout />}>
                <Route path="/preview/:token" element={<HomePage />} />
                <Route path="/preview/:token/fonctionnement" element={<FonctionnementPage />} />
                <Route path="/preview/:token/fonctionnalites" element={<FonctionnalitesPage />} />
                <Route path="/preview/:token/avis" element={<AvisPage />} />
                <Route path="/preview/:token/boutique" element={<BoutiquePage />} />
            </Route>
            <Route path="/connexion" element={<LoginPage />} />
            <Route path="/review-return" element={<ReviewReturnPage />} />
            <Route path="/reinitialiser" element={<ResetPasswordPage />} />
            <Route path="/mon-compte" element={<MyAccountPage />} />
            <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="audience" element={<Audience />} />
                <Route path="utilisateurs" element={<Users />} />
                <Route path="medias" element={<Media />} />
                <Route path="contenu" element={<Content />} />
                <Route path="faq" element={<Faq />} />
                <Route path="tarifs" element={<Offers />} />
                <Route path="boutique" element={<Boutique />} />
                <Route path="telechargements" element={<StoreLinks />} />
                <Route path="temoignages" element={<Testimonials />} />
                <Route path="messages" element={<Messages />} />
                <Route path="audit" element={<Audit />} />
                <Route path="parametres" element={<Settings />} />
                <Route path="compte" element={<Profile />} />
            </Route>
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

function App() {
    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <SiteProvider>
                        <ScrollToTop />
                        <PageTracker />
                        <AppRouter />
                        <Toaster richColors position="top-right" />
                    </SiteProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
